<template>
  <HeaderNavbar :title="$t('billing.vbudgets.title')" :breadcrumbs="listBreadcrumbs" :no-bottom-margin="true">
    <template #right-side>
      <button class="btn btn-primary" @click="openCreateModal(selectedNode || undefined)" :disabled="!activeBudget">
        <IconPlus size="16" class="me-1" />
        {{ $t('billing.vbudgets.create_vbudget') }}
      </button>
    </template>
  </HeaderNavbar>

  <SidebarLayout sidebar-width="340px">
    <template #search>
      <LiFormInput v-model="searchQuery" icon="ti ti-search" :placeholder="$t('search')" />
    </template>

    <template #sidebar>
      <VBudgetTreeSidebar
        :tree="budgetTree"
        :selected-node-id="selectedNodeId"
        :search-query="searchQuery"
        :currency="activeBudget?.currency || 'CHF'"
        @update:selected-node-id="selectedNodeId = $event" />
    </template>

    <VBudgetDetailPanel
      v-if="selectedNode"
      :node="selectedNode"
      :is-root="isSelectedRoot"
      :is-editable="isNodeEditable"
      :is-deletable="isNodeDeletable"
      :currency="selectedCurrency"
      :transactions-endpoint="vbudgetTransactionsEndpoint"
      :subtitle="selectedNodeSubtitle"
      :detail-properties="detailProperties"
      :bar-segments="barSegments"
      :allocated-amount="selectedAllocatedAmount"
      @edit="openEditModal"
      @delete="confirmDelete" />

    <!-- Empty / no selection state -->
    <div v-else class="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
      <IconWallet size="56" class="mb-3 opacity-25" />
      <h5>{{ $t('billing.vbudgets.select_budget') }}</h5>
      <p class="text-center" style="max-width: 300px">{{ $t('billing.vbudgets.select_budget_description') }}</p>
    </div>
  </SidebarLayout>

  <VBudgetFormModal
    v-model="showFormModal"
    :mode="formMode"
    :parent-node="formParentNode"
    v-model:form="formData"
    :date-range="dateRange"
    v-model:limit-availability="limitAvailability"
    :currency="activeBudget?.currency || 'CHF'"
    :errors="errors"
    @update:date-range="dateRange = $event"
    @save="saveVBudget"
    @clearError="clearError" />

  <LiConfirmationDialog
    v-model:dialogShow="showDeleteModal"
    :confirmButtonLabel="$t('btn.delete')"
    @onContinue="deleteCurrentVBudget">
    <h1 class="mb-3">{{ $t('billing.vbudgets.delete_confirm_title') }}</h1>
    <p>{{ $t('billing.vbudgets.delete_confirm_description') }}</p>
  </LiConfirmationDialog>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { FormInput as LiFormInput, ConfirmationDialog as LiConfirmationDialog } from '@prospective/lithium';
import { IconPlus, IconWallet } from '@tabler/icons-vue';

import SidebarLayout from '@/components/common/SidebarLayout.vue';
import HeaderNavbar from '@/components/header/HeaderNavbar.vue';
import VBudgetFormModal from '@/components/budget/VBudgetFormModal.vue';
import VBudgetTreeSidebar from '@/components/budget/VBudgetTreeSidebar.vue';
import VBudgetDetailPanel from '@/components/budget/VBudgetDetailPanel.vue';

import translator from '@/locale/translator';
import { useAutopilotFunctions } from '@/composables/useAutopilotFunctions';
import { useAutopilotContext } from '@/composables/useAutopilotContext';
import { useAutopilotRouteReady } from '@/composables/useAutopilotRouteReady';
import { vbudgetWorkflowGuide } from '@/autopilot/functions/vbudgets/vbudgetWorkflow';
import type { AutopilotFunctionDefinition } from '@llmor/autopilot-core';
import { fetchActiveBudget, fetchBudgetTree, createVBudget, updateVBudget, deleteVBudget } from '@/api/budget';
import { formatCurrency } from '@/locale/format';
import { formatDate, formatDateTime, PJAS_DATE_FORMAT } from '@/filters/dateFormat';
import { getBudgetNodeId, getBudgetNodeCurrency } from '@/helpers/budgetUtils';
import { useToast } from 'vue-toastification';

import type { IBudgetTree, IBudgetTreeNode, IActiveBudget } from '@/types/budget';
import type { PropertyItem } from '@/components/common/PropertyList.vue';

const toast = useToast();

const listBreadcrumbs = computed(() => [
  { label: translator.get('nav.billing'), path: { name: 'billing' } },
  { label: translator.get('nav.billing.budget'), path: { name: 'billing.budget' } },
  { label: translator.get('nav.billing.vbudgets'), path: { name: 'billing.vbudgets' } },
]);

/* --------------------------------- STATE -------------------------------- */
const budgetTree = ref<IBudgetTree>();
const activeBudget = ref<IActiveBudget>();
const searchQuery = ref('');
const selectedNodeId = ref<string | null>(null);

const showFormModal = ref(false);
const showDeleteModal = ref(false);
const formMode = ref<'create' | 'edit'>('create');
const formParentNode = ref<IBudgetTreeNode | null>(null);
const nodeToDelete = ref<IBudgetTreeNode | null>(null);

const defaultFormData = () => ({
  id: undefined as number | undefined,
  name: '',
  description: '',
  allocation: 0,
  allocation_type: 'fixed' as 'fixed' | 'percentage',
  reference_channels: [] as string[],
});

const formData = ref(defaultFormData());
const dateRange = ref<[Date, Date] | null>(null);
const limitAvailability = ref(false);
const errors = ref<Record<string, string[]>>({});

/* -------------------------------- COMPUTED -------------------------------- */
const selectedNode = computed(() => {
  if (!selectedNodeId.value || !budgetTree.value?.tree) return null;
  const root = budgetTree.value.tree;
  if (getBudgetNodeId(root) === selectedNodeId.value) return root;
  const match = selectedNodeId.value.match(/^vbudget_(\d+)$/);
  if (match) return findNodeById(root, parseInt(match[1]));
  return null;
});

const isSelectedRoot = computed(() => selectedNode.value?.type === 'customer_budget');

const isNodeEditable = computed(() => {
  if (!selectedNode.value || isSelectedRoot.value) return false;
  return selectedNode.value.availability.total_spent < selectedNode.value.availability.available_initial;
});

const isNodeDeletable = computed(() => {
  if (!selectedNode.value || isSelectedRoot.value) return false;
  return (selectedNode.value.availability.total_spent || 0) === 0;
});

const vbudgetTransactionsEndpoint = computed(() => {
  if (!activeBudget.value || !selectedNode.value?.vbudget?.data) return '';
  const budgetId = activeBudget.value.id;
  const vbudgetId = selectedNode.value.vbudget.data.id;
  return `/v1/account/budgets/${budgetId}/vbudgets/${vbudgetId}/transactions`;
});

const selectedNodeSubtitle = computed(() => {
  const data = selectedNode.value?.vbudget?.data;
  if (!data) return undefined;
  const parts: string[] = [];
  if (data.startdate && data.enddate) {
    parts.push(`${formatDate(data.startdate)} – ${formatDate(data.enddate)}`);
  } else if (data.startdate) {
    parts.push(`${translator.get('billing.vbudgets.start_date')}: ${formatDate(data.startdate)}`);
  } else if (data.enddate) {
    parts.push(`${translator.get('billing.vbudgets.end_date')}: ${formatDate(data.enddate)}`);
  }
  if (data.description) parts.push(data.description);
  return parts.join(' · ') || undefined;
});

const selectedCurrency = computed(() =>
  selectedNode.value ? getBudgetNodeCurrency(selectedNode.value, activeBudget.value?.currency || 'CHF') : 'CHF'
);

const selectedAllocatedAmount = computed(() => {
  if (!selectedNode.value) return 0;
  return selectedNode.value.availability.allocated_to_children;
});

const barSegments = computed(() => {
  if (!selectedNode.value) return { spent: 0, allocated: 0 };
  const a = selectedNode.value.availability;
  const total = a.available_initial;
  if (total <= 0) return { spent: 0, allocated: 0 };
  const spent = Math.min((a.total_spent / total) * 100, 100);
  const allocated = isSelectedRoot.value ? (selectedAllocatedAmount.value / total) * 100 : 0;
  return { spent, allocated: Math.max(0, Math.min(allocated, 100 - spent)) };
});

const detailProperties = computed<PropertyItem[]>(() => {
  if (!selectedNode.value) return [];
  const node = selectedNode.value;
  const items: PropertyItem[] = [
    {
      label: translator.get('billing.vbudgets.description'),
      value:
        node.customer_budget?.data.description ||
        node.vbudget?.data.description ||
        translator.get('billing.vbudgets.no_description'),
    },
  ];
  if (!isSelectedRoot.value) {
    items.push({
      label: translator.get('billing.vbudgets.allocation'),
      value: formatCurrency(node.availability.available_initial, selectedCurrency.value),
    });
  }
  const startdate = node.customer_budget?.data.startdate || node.vbudget?.data.startdate;
  const enddate = node.customer_budget?.data.enddate || node.vbudget?.data.enddate;
  items.push(
    { label: translator.get('billing.vbudgets.start_date'), value: startdate ? formatDate(startdate) : '–' },
    { label: translator.get('billing.vbudgets.end_date'), value: enddate ? formatDate(enddate) : '–' }
  );
  return items;
});

/* -------------------------------- LIFECYCLE -------------------------------- */
onMounted(async () => {
  await loadActiveBudget();
  if (activeBudget.value) {
    await loadBudgetTree();
  }
  routeReady();
});

/* ------------------------------- DATA LOADING ------------------------------- */
async function loadActiveBudget() {
  try {
    const response = await fetchActiveBudget();
    activeBudget.value = response.data;
    syncAutopilotContext();
  } catch (error) {
    console.error('Error fetching active budget:', error);
    toast.error(translator.get('billing.vbudgets.error_loading_budget'));
  }
}

async function loadBudgetTree() {
  if (!activeBudget.value?.id) return;
  try {
    const response = await fetchBudgetTree(activeBudget.value.id);
    budgetTree.value = response.data || response;
    if (budgetTree.value?.tree && !selectedNodeId.value) {
      selectedNodeId.value = getBudgetNodeId(budgetTree.value.tree);
    }
    syncAutopilotContext();
  } catch (error) {
    console.error('Error fetching budget tree:', error);
    toast.error(translator.get('billing.vbudgets.error_loading_tree'));
  }
}

/* ------------------------------- MODAL MANAGEMENT ------------------------------- */
function openCreateModal(parentNode?: IBudgetTreeNode) {
  errors.value = {};
  formMode.value = 'create';
  formParentNode.value = parentNode || budgetTree.value?.tree || null;
  formData.value = defaultFormData();
  dateRange.value = null;
  limitAvailability.value = false;
  showFormModal.value = true;
}

function openEditModal(node: IBudgetTreeNode) {
  if (node.type === 'customer_budget' || !node.vbudget?.data) return;

  errors.value = {};
  formMode.value = 'edit';
  formParentNode.value = findParentNode(budgetTree.value!.tree, node) || budgetTree.value!.tree;

  const data = node.vbudget.data;
  formData.value = {
    id: data.id,
    name: data.name || '',
    description: data.description || '',
    allocation: data.allocation || 0,
    allocation_type: data.allocation_type || 'fixed',
    reference_channels: data.reference_channels || [],
  };
  const hasDates = !!(data.startdate && data.enddate);
  limitAvailability.value = hasDates;
  dateRange.value = hasDates ? [new Date(data.startdate!), new Date(data.enddate!)] : null;
  showFormModal.value = true;
}

function confirmDelete(node: IBudgetTreeNode) {
  nodeToDelete.value = node;
  showDeleteModal.value = true;
}

function clearError(field: string) {
  if (!errors.value || !(field in errors.value)) return;
  delete errors.value[field];
}

/* ------------------------------- CRUD ------------------------------- */
async function saveVBudget() {
  errors.value = {};
  if (!activeBudget.value?.id || !formData.value.name || !formData.value.allocation) {
    toast.error(translator.get('billing.vbudgets.form_validation_error'));
    return;
  }

  try {
    const payload: Record<string, unknown> = {
      name: formData.value.name,
      description: formData.value.description,
      allocation: formData.value.allocation,
      allocation_type: 'fixed',
      allocation_currency: activeBudget.value.currency,
      startdate: dateRange.value ? formatDateTime(dateRange.value[0], PJAS_DATE_FORMAT) : null,
      enddate: dateRange.value ? formatDateTime(dateRange.value[1], PJAS_DATE_FORMAT) : null,
      reference_channels: formData.value.reference_channels,
      parent_vbudget_id: null,
    };

    if (formData.value.id) {
      payload.id = formData.value.id;
      const editNode = findNodeById(budgetTree.value!.tree, formData.value.id);
      payload.parent_vbudget_id = editNode?.vbudget?.data.parent_id || null;
      await updateVBudget(activeBudget.value.id, formData.value.id, payload);
      toast.success(translator.get('billing.vbudgets.updated_successfully'));
    } else {
      payload.parent_vbudget_id =
        formParentNode.value?.type === 'vbudget' ? formParentNode.value.vbudget?.data.id || null : null;
      await createVBudget(activeBudget.value.id, payload);
      toast.success(translator.get('billing.vbudgets.created_successfully'));
    }

    showFormModal.value = false;
    await loadBudgetTree();
  } catch (e: any) {
    console.error('Error saving the virtual budget:', e);
    errors.value = e.responseJson?.errors || {};
  }
}

async function deleteCurrentVBudget() {
  if (!nodeToDelete.value?.vbudget?.data || !activeBudget.value) return;

  try {
    await deleteVBudget(activeBudget.value.id, nodeToDelete.value.vbudget.data.id);
    toast.success(translator.get('billing.vbudgets.deleted_successfully'));
    showDeleteModal.value = false;
    selectedNodeId.value = getBudgetNodeId(budgetTree.value!.tree);
    nodeToDelete.value = null;
    await loadBudgetTree();
  } catch (error) {
    console.error('Error deleting the virtual budget:', error);
    toast.error(translator.get('billing.vbudgets.error_deleting'));
  }
}

/* ------------------------------- TREE HELPERS ------------------------------- */
function findParentNode(root: IBudgetTreeNode, target: IBudgetTreeNode): IBudgetTreeNode | null {
  const targetId = target.vbudget?.data.id;
  if (!targetId) return null;
  for (const child of root.children || []) {
    if (child.vbudget?.data.id === targetId) return root;
    const found = findParentNode(child as IBudgetTreeNode, target);
    if (found) return found;
  }
  return null;
}

function findNodeById(root: IBudgetTreeNode, vbudgetId: number): IBudgetTreeNode | null {
  if (root.vbudget?.data.id === vbudgetId) return root;
  for (const child of root.children || []) {
    const found = findNodeById(child as IBudgetTreeNode, vbudgetId);
    if (found) return found;
  }
  return null;
}

/* -------------------------------- AUTOPILOT ------------------------------- */
const autopilotContextData = reactive({
  name: 'virtual_budgets',
  description:
    'Virtual Budgets page. User can view a hierarchical budget tree, create/edit/delete virtual budgets, and inspect budget allocation and spending details. All monetary amounts are in cents (e.g. 500000 = 5000.00 CHF).',
  activeBudgetInfo: null as null | {
    id: number;
    name: string;
    currency: string;
    amount: number;
    startdate: string;
    enddate: string;
  },
  selectedNodeId: null as string | null,
  selectedNode: null as null | {
    id: string;
    type: string;
    name: string;
    description: string;
    allocation: number;
    currency: string;
    available_initial: number;
    available_now: number;
    available_unallocated: number;
    total_spent: number;
    startdate: string | null;
    enddate: string | null;
    isEditable: boolean;
    isDeletable: boolean;
    parentId: number | null;
  },
  budgetTreeSummary: [] as Array<{
    id: string;
    vbudgetId: number | null;
    name: string;
    type: string;
    allocation: number;
    total_spent: number;
    available_now: number;
    parentId: number | null;
    childCount: number;
    depth: number;
  }>,
  totalVBudgets: 0,
});

function flattenBudgetTree(node: IBudgetTreeNode, depth = 0): typeof autopilotContextData.budgetTreeSummary {
  const result: typeof autopilotContextData.budgetTreeSummary = [];
  const nodeId = getBudgetNodeId(node);
  const name =
    node.type === 'customer_budget'
      ? node.customer_budget?.data.name || 'Root Budget'
      : node.vbudget?.data.name || 'Unknown';

  result.push({
    id: nodeId,
    vbudgetId: node.vbudget?.data.id ?? null,
    name,
    type: node.type,
    allocation: node.availability.available_initial,
    total_spent: node.availability.total_spent,
    available_now: node.availability.available_now,
    parentId: node.vbudget?.data.parent_id ?? null,
    childCount: node.children?.length ?? 0,
    depth,
  });

  for (const child of node.children || []) {
    result.push(...flattenBudgetTree(child as IBudgetTreeNode, depth + 1));
  }
  return result;
}

function syncAutopilotContext() {
  if (activeBudget.value) {
    autopilotContextData.activeBudgetInfo = {
      id: activeBudget.value.id,
      name: activeBudget.value.name,
      currency: activeBudget.value.currency,
      amount: activeBudget.value.amount,
      startdate: activeBudget.value.startdate,
      enddate: activeBudget.value.enddate,
    };
  } else {
    autopilotContextData.activeBudgetInfo = null;
  }

  if (budgetTree.value?.tree) {
    const flat = flattenBudgetTree(budgetTree.value.tree);
    autopilotContextData.budgetTreeSummary = flat;
    autopilotContextData.totalVBudgets = flat.filter((n) => n.type === 'vbudget').length;
  } else {
    autopilotContextData.budgetTreeSummary = [];
    autopilotContextData.totalVBudgets = 0;
  }

  autopilotContextData.selectedNodeId = selectedNodeId.value;
  const node = selectedNode.value;
  if (node) {
    const currency = getBudgetNodeCurrency(node, activeBudget.value?.currency || 'CHF');
    autopilotContextData.selectedNode = {
      id: getBudgetNodeId(node),
      type: node.type,
      name: node.type === 'customer_budget' ? node.customer_budget?.data.name || '' : node.vbudget?.data.name || '',
      description: node.customer_budget?.data.description || node.vbudget?.data.description || '',
      allocation: node.availability.available_initial,
      currency,
      available_initial: node.availability.available_initial,
      available_now: node.availability.available_now,
      available_unallocated: node.availability.available_unallocated,
      total_spent: node.availability.total_spent,
      startdate: node.customer_budget?.data.startdate || node.vbudget?.data.startdate || null,
      enddate: node.customer_budget?.data.enddate || node.vbudget?.data.enddate || null,
      isEditable: isNodeEditable.value,
      isDeletable: isNodeDeletable.value,
      parentId: node.vbudget?.data.parent_id ?? null,
    };
  } else {
    autopilotContextData.selectedNode = null;
  }
}

const routeReady = useAutopilotRouteReady();
useAutopilotContext('vbudget_workflow', () => vbudgetWorkflowGuide);
useAutopilotContext('current_page', () => ({ ...autopilotContextData }));

watch(selectedNodeId, () => {
  syncAutopilotContext();
});

const getVirtualBudgets: AutopilotFunctionDefinition = {
  name: 'get_virtual_budgets',
  description:
    'Get information about virtual budgets. Can list all budgets in the tree, search by name, or get details of a specific budget by its vbudget ID. Can also select a node in the sidebar UI.',
  parameters: {
    type: 'object',
    properties: {
      search: {
        type: 'string',
        description: 'Search virtual budgets by name (case-insensitive partial match)',
      },
      vbudget_id: {
        type: 'number',
        description: 'Get details of a specific virtual budget by its ID',
      },
      select_node: {
        type: 'string',
        description:
          'Select a node in the sidebar by its node ID (e.g. "vbudget_123" or "budget_456"). Updates the UI to show that node\'s details.',
      },
    },
    required: [],
  },
  handler: async (rawArgs) => {
    const args = (rawArgs ?? {}) as { search?: string; vbudget_id?: number; select_node?: string };
    if (args.select_node) {
      selectedNodeId.value = args.select_node;
    }

    if (args.vbudget_id && budgetTree.value?.tree) {
      const node = findNodeById(budgetTree.value.tree, args.vbudget_id);
      if (!node) {
        return { success: false, error: `Virtual budget with ID ${args.vbudget_id} not found.` };
      }
      return {
        success: true,
        vbudget: {
          id: node.vbudget?.data.id,
          name: node.vbudget?.data.name,
          description: node.vbudget?.data.description,
          allocation: node.vbudget?.data.allocation,
          allocation_type: node.vbudget?.data.allocation_type,
          allocation_currency: node.vbudget?.data.allocation_currency,
          startdate: node.vbudget?.data.startdate,
          enddate: node.vbudget?.data.enddate,
          parent_id: node.vbudget?.data.parent_id,
          reference_channels: node.vbudget?.data.reference_channels,
          availability: node.availability,
          childCount: node.children?.length ?? 0,
        },
      };
    }

    let results = autopilotContextData.budgetTreeSummary;
    if (args.search) {
      const query = args.search.toLowerCase();
      results = results.filter((n) => n.name.toLowerCase().includes(query));
    }

    return {
      success: true,
      activeBudget: autopilotContextData.activeBudgetInfo,
      selectedNodeId: selectedNodeId.value,
      totalVBudgets: autopilotContextData.totalVBudgets,
      budgets: results,
    };
  },
};

const createVirtualBudgetFn: AutopilotFunctionDefinition = {
  name: 'create_virtual_budget',
  description:
    'Create a new virtual budget. The budget will be created as a child of the specified parent. If no parent_vbudget_id is provided, it will be created under the root customer budget.',
  parameters: {
    type: 'object',
    properties: {
      name: { type: 'string', description: 'Name of the virtual budget (required)' },
      description: { type: 'string', description: 'Description of the virtual budget' },
      allocation: {
        type: 'number',
        description: 'Budget allocation amount in cents (e.g. 5000 CHF = 500000). Required.',
      },
      startdate: {
        type: 'string',
        description:
          'Optional start date in YYYY-MM-DD format. If provided, must be in the future. Limits when the budget becomes available.',
      },
      enddate: {
        type: 'string',
        description:
          'Optional end date in YYYY-MM-DD format. If provided, limits when the budget expires. Must be after startdate if both are set.',
      },
      parent_vbudget_id: {
        type: 'number',
        description: 'ID of the parent virtual budget. Omit or null to create under the root customer budget.',
      },
      reference_channels: {
        type: 'array',
        items: { type: 'string' },
        description: 'List of reference channel identifiers to associate with this budget',
      },
    },
    required: ['name', 'allocation'],
  },
  handler: async (rawArgs) => {
    const args = (rawArgs ?? {}) as {
      name: string;
      description?: string;
      allocation: number;
      startdate?: string;
      enddate?: string;
      parent_vbudget_id?: number;
      reference_channels?: string[];
    };
    if (!activeBudget.value?.id) {
      return { success: false, error: 'No active customer budget found. Cannot create virtual budget.' };
    }

    try {
      const payload: Record<string, unknown> = {
        name: args.name,
        description: args.description || '',
        allocation: args.allocation,
        allocation_type: 'fixed',
        allocation_currency: activeBudget.value.currency,
        startdate: args.startdate || null,
        enddate: args.enddate || null,
        parent_vbudget_id: args.parent_vbudget_id || null,
        reference_channels: args.reference_channels || [],
      };

      const response = await createVBudget(activeBudget.value.id, payload);
      toast.success(translator.get('billing.vbudgets.created_successfully'));
      await loadBudgetTree();

      if (response.data?.id) {
        selectedNodeId.value = `vbudget_${response.data.id}`;
      }

      return {
        success: true,
        message: `Virtual budget "${args.name}" created successfully.`,
        vbudget: response.data,
      };
    } catch (e: any) {
      return {
        success: false,
        error: 'Failed to create virtual budget.',
        validationErrors: e.responseJson?.errors || {},
      };
    }
  },
};

const updateVirtualBudgetFn: AutopilotFunctionDefinition = {
  name: 'update_virtual_budget',
  description:
    'Update an existing virtual budget. Only pass the fields you want to change. The vbudget_id is required to identify which budget to update.',
  parameters: {
    type: 'object',
    properties: {
      vbudget_id: { type: 'number', description: 'ID of the virtual budget to update (required)' },
      name: { type: 'string', description: 'New name for the virtual budget' },
      description: { type: 'string', description: 'New description' },
      allocation: { type: 'number', description: 'New allocation amount in cents (e.g. 5000 CHF = 500000)' },
      startdate: {
        type: 'string',
        description:
          'Optional new start date in YYYY-MM-DD format. If provided, must be in the future. Pass empty string to clear.',
      },
      enddate: {
        type: 'string',
        description:
          'Optional new end date in YYYY-MM-DD format. Must be after startdate if both are set. Pass empty string to clear.',
      },
      reference_channels: {
        type: 'array',
        items: { type: 'string' },
        description: 'New list of reference channel identifiers',
      },
    },
    required: ['vbudget_id'],
  },
  handler: async (rawArgs) => {
    const args = (rawArgs ?? {}) as {
      vbudget_id: number;
      name?: string;
      description?: string;
      allocation?: number;
      startdate?: string;
      enddate?: string;
      reference_channels?: string[];
    };
    if (!activeBudget.value?.id) {
      return { success: false, error: 'No active customer budget found.' };
    }
    if (!budgetTree.value?.tree) {
      return { success: false, error: 'Budget tree not loaded.' };
    }

    const node = findNodeById(budgetTree.value.tree, args.vbudget_id);
    if (!node || !node.vbudget?.data) {
      return { success: false, error: `Virtual budget with ID ${args.vbudget_id} not found.` };
    }

    if (node.availability.total_spent >= node.availability.available_initial) {
      return { success: false, error: 'This virtual budget cannot be edited because it has been fully spent.' };
    }

    try {
      const existingData = node.vbudget.data;
      const payload: Record<string, unknown> = {
        id: args.vbudget_id,
        name: args.name ?? existingData.name,
        description: args.description ?? existingData.description,
        allocation: args.allocation ?? existingData.allocation,
        allocation_type: 'fixed',
        allocation_currency: activeBudget.value.currency,
        startdate: args.startdate !== undefined ? args.startdate || null : existingData.startdate,
        enddate: args.enddate !== undefined ? args.enddate || null : existingData.enddate,
        parent_vbudget_id: existingData.parent_id,
        reference_channels: args.reference_channels ?? existingData.reference_channels,
      };

      await updateVBudget(activeBudget.value.id, args.vbudget_id, payload);
      toast.success(translator.get('billing.vbudgets.updated_successfully'));
      await loadBudgetTree();

      return {
        success: true,
        message: `Virtual budget "${payload.name}" updated successfully.`,
      };
    } catch (e: any) {
      return {
        success: false,
        error: 'Failed to update virtual budget.',
        validationErrors: e.responseJson?.errors || {},
      };
    }
  },
};

const deleteVirtualBudgetFn: AutopilotFunctionDefinition = {
  name: 'delete_virtual_budget',
  description:
    'Delete an existing virtual budget. The budget can only be deleted if it has no spending (total_spent is 0). Cannot delete the root customer budget.',
  parameters: {
    type: 'object',
    properties: {
      vbudget_id: { type: 'number', description: 'ID of the virtual budget to delete (required)' },
    },
    required: ['vbudget_id'],
  },
  handler: async (rawArgs) => {
    const args = (rawArgs ?? {}) as { vbudget_id: number };
    if (!activeBudget.value?.id) {
      return { success: false, error: 'No active customer budget found.' };
    }
    if (!budgetTree.value?.tree) {
      return { success: false, error: 'Budget tree not loaded.' };
    }

    const node = findNodeById(budgetTree.value.tree, args.vbudget_id);
    if (!node || !node.vbudget?.data) {
      return { success: false, error: `Virtual budget with ID ${args.vbudget_id} not found.` };
    }

    if ((node.availability.total_spent || 0) > 0) {
      return {
        success: false,
        error: 'This virtual budget cannot be deleted because it has spending associated with it.',
      };
    }

    try {
      await deleteVBudget(activeBudget.value.id, args.vbudget_id);
      toast.success(translator.get('billing.vbudgets.deleted_successfully'));
      selectedNodeId.value = getBudgetNodeId(budgetTree.value!.tree);
      await loadBudgetTree();

      return {
        success: true,
        message: `Virtual budget "${node.vbudget.data.name}" deleted successfully.`,
      };
    } catch (e: any) {
      return {
        success: false,
        error: 'Failed to delete virtual budget.',
        details: e.responseJson?.errors || e.message,
      };
    }
  },
};

createVirtualBudgetFn.sensitive = true;
updateVirtualBudgetFn.sensitive = true;
deleteVirtualBudgetFn.sensitive = true;

useAutopilotFunctions([getVirtualBudgets, createVirtualBudgetFn, updateVirtualBudgetFn, deleteVirtualBudgetFn]);
</script>
